/**
 * セクション追加ダイアログ。
 * - 「セクションタイプ」タブ: type → variant を選んで sample content で挿入
 * - 「テンプレート」タブ:    landingSectionTemplates から content を複製して挿入
 */
import { Bookmark, Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import {
  useDeleteLandingSectionTemplate,
  useLandingSectionTemplates,
  type LandingSectionTemplateRecord,
} from '@/hooks/useLandingSectionTemplates';
import { useUpdateLanding, type LandingRecord } from '@/hooks/useLandings';
import { db } from '@/lib/firebase';

import {
  SECTIONS,
  SECTION_TYPES,
  getSectionComponent,
  type SectionType,
  type VisualIdentity,
} from '@goenhub/landing-sections';
import { identityVars } from '@goenhub/landing-sections/identities';
import type { LandingSectionRef } from '@goenhub/shared';

// SECTIONS の label をそのまま使う（人物紹介 / ヒーロー / 等）
const typeLabel = (type: SectionType): string => SECTIONS[type]?.label ?? type;
const variantLabel = (type: SectionType, variant: string): string =>
  SECTIONS[type]?.variantLabels?.[variant] ?? variant;

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  landingId: string;
  landing: LandingRecord;
}

type Tab = 'type' | 'template';

export function AddSectionDialog({ open, onOpenChange, landingId, landing }: Props) {
  const [tab, setTab] = useState<Tab>('type');
  const [type, setType] = useState<SectionType | null>(null);
  const updateLanding = useUpdateLanding();
  const templatesQuery = useLandingSectionTemplates();
  const deleteTemplate = useDeleteLandingSectionTemplate();

  const handleClose = () => {
    setType(null);
    setTab('type');
    onOpenChange(false);
  };

  /** 既存 sections と衝突しない新 ID を生成 */
  const nextSectionId = (): string => {
    const existingIds = new Set(landing.sections.map((s) => s.id));
    let n = (landing.sections.length ?? 0) + 1;
    while (existingIds.has(`s${n}`)) n++;
    return `s${n}`;
  };

  const handleAddFromSample = async (variant: string) => {
    if (!type) return;
    const sectionDef = SECTIONS[type];
    if (!sectionDef) return;
    const sample = sectionDef.sample as Record<string, unknown>;

    const newId = nextSectionId();
    const order = landing.sections.length;

    const titleStr =
      typeof sample.headline === 'string'
        ? (sample.headline as string)
        : typeof sample.brand === 'string'
          ? (sample.brand as string)
          : typeLabel(type);

    const newRef: LandingSectionRef = {
      id: newId,
      type,
      variant,
      order,
      status: 'ready',
      title: titleStr,
      wordCount: 0,
    };

    try {
      await setDoc(doc(db, 'landings', landingId, 'sections', newId), {
        type,
        variant,
        order,
        content: sample,
        imageRefs: {},
        updatedAt: serverTimestamp(),
      });
      await updateLanding.mutateAsync({
        id: landingId,
        data: { sections: [...landing.sections, newRef] },
      });
      toast.success(`${typeLabel(type)} を追加しました`);
      handleClose();
    } catch (e) {
      toast.error(`追加失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleAddFromTemplate = async (tpl: LandingSectionTemplateRecord) => {
    const newId = nextSectionId();
    const order = landing.sections.length;
    const content = tpl.content ?? {};
    const titleStr =
      typeof content.headline === 'string'
        ? (content.headline as string)
        : typeof content.brand === 'string'
          ? (content.brand as string)
          : tpl.name;

    const newRef: LandingSectionRef = {
      id: newId,
      type: tpl.type,
      variant: tpl.variant,
      order,
      status: 'ready',
      title: titleStr,
      wordCount: 0,
    };

    try {
      await setDoc(doc(db, 'landings', landingId, 'sections', newId), {
        type: tpl.type,
        variant: tpl.variant,
        order,
        content,
        style: tpl.style ?? null,
        imageRefs: {},
        updatedAt: serverTimestamp(),
      });
      await updateLanding.mutateAsync({
        id: landingId,
        data: { sections: [...landing.sections, newRef] },
      });
      toast.success(`テンプレ「${tpl.name}」を追加しました`);
      handleClose();
    } catch (e) {
      toast.error(`追加失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleDeleteTemplate = async (tpl: LandingSectionTemplateRecord) => {
    if (!confirm(`テンプレ「${tpl.name}」を削除しますか？`)) return;
    try {
      await deleteTemplate.mutateAsync(tpl.id);
      toast.success('テンプレを削除しました');
    } catch (e) {
      toast.error(`削除失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>セクション追加</DialogTitle>
          <DialogDescription>
            {tab === 'template'
              ? '保存済みテンプレートから挿入できます。'
              : type
                ? `${typeLabel(type)} のバリアントを選択`
                : 'セクションタイプを選択'}
          </DialogDescription>
        </DialogHeader>

        {/* タブ切替 */}
        <div className="flex gap-1 border-b border-[var(--border)] mb-3">
          <button
            type="button"
            onClick={() => {
              setTab('type');
              setType(null);
            }}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors ${
              tab === 'type'
                ? 'border-[var(--accent)] text-[var(--accent)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            セクションタイプ
          </button>
          <button
            type="button"
            onClick={() => setTab('template')}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors flex items-center gap-1 ${
              tab === 'template'
                ? 'border-[var(--accent)] text-[var(--accent)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            }`}
          >
            <Bookmark className="size-3" />
            テンプレート
            {templatesQuery.data && templatesQuery.data.length > 0 && (
              <span className="ml-1 text-[10px] text-[var(--text-muted)]">
                ({templatesQuery.data.length})
              </span>
            )}
          </button>
        </div>

        {tab === 'template' && (
          <TemplateGrid
            templates={templatesQuery.data ?? []}
            isLoading={templatesQuery.isLoading}
            onPick={handleAddFromTemplate}
            onDelete={handleDeleteTemplate}
            disabled={updateLanding.isPending}
            visualIdentity={landing.visualIdentity as VisualIdentity}
          />
        )}

        {tab === 'type' && !type && (
          <div className="grid grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto py-2">
            {SECTION_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className="px-3 py-3 text-sm text-left border border-[var(--border)] rounded-md hover:bg-[var(--bg-hover)] hover:border-[var(--accent)] transition-colors"
              >
                <div className="font-medium">{typeLabel(t)}</div>
              </button>
            ))}
          </div>
        )}

        {tab === 'type' && type && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto py-2">
              {Object.keys(SECTIONS[type].variants).map((v) => (
                <VariantPreviewCard
                  key={v}
                  type={type}
                  variant={v}
                  onPick={() => handleAddFromSample(v)}
                  disabled={updateLanding.isPending}
                  visualIdentity={landing.visualIdentity as VisualIdentity}
                />
              ))}
            </div>
            <div className="flex justify-between mt-2">
              <Button variant="ghost" size="sm" onClick={() => setType(null)}>
                ← タイプ選択へ戻る
              </Button>
              <Button variant="ghost" size="sm" onClick={handleClose}>
                キャンセル
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * セクションタイプ + variant の組合せを縮小プレビュー付きで選択させるカード。
 * サンプル content で実コンポーネントをレンダーするので、見た目で variant を選べる。
 */
function VariantPreviewCard({
  type,
  variant,
  onPick,
  disabled,
  visualIdentity,
}: {
  type: SectionType;
  variant: string;
  onPick: () => void;
  disabled: boolean;
  visualIdentity: VisualIdentity | undefined;
}) {
  const sectionDef = SECTIONS[type];
  const Comp = sectionDef?.variants?.[variant];
  const sample = sectionDef?.sample as Record<string, unknown> | undefined;
  const identity: VisualIdentity = visualIdentity ?? 'light';
  const identityCss = identityVars(identity, null);
  const lbl = variantLabel(type, variant);

  return (
    <div className="border border-[var(--border)] rounded-md bg-[var(--bg-elevated)] overflow-hidden flex flex-col hover:border-[var(--accent)] transition-colors">
      {/* 縮小プレビュー */}
      <div
        className="relative bg-[var(--bg-root)] border-b border-[var(--border)] overflow-hidden cursor-pointer"
        style={{ height: 180 }}
        onClick={() => !disabled && onPick()}
        title={`${lbl} を挿入`}
      >
        {Comp && sample ? (
          <ScaledPreview referenceWidth={1280}>
            <div
              data-ls-identity={identity}
              style={
                {
                  ...(identityCss as Record<string, string>),
                  width: 1280,
                } as React.CSSProperties
              }
            >
              <Comp content={sample as never} />
            </div>
          </ScaledPreview>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[var(--text-muted)]">
            プレビュー描画不可
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-black/60 text-white z-10">
          {lbl}
        </div>
      </div>
      <div className="p-2.5 flex items-center justify-between gap-2">
        <div className="text-[10px] text-[var(--text-muted)] font-mono truncate">{variant}</div>
        <Button size="sm" onClick={onPick} disabled={disabled}>
          <Plus className="size-3.5" /> 追加
        </Button>
      </div>
    </div>
  );
}

/**
 * 縮小プレビューラッパ。
 * 親の幅を測定し、`referenceWidth` (1280px 想定のデスクトップ幅) に対する
 * 比率で transform: scale() を計算してフィットさせる。
 * 結果: カードに左右余白なく実コンポーネントを縮小表示できる。
 */
function ScaledPreview({
  children,
  referenceWidth,
}: {
  children: ReactNode;
  referenceWidth: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.25);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / referenceWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [referenceWidth]);

  return (
    <div
      ref={ref}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <div
        style={{
          width: referenceWidth,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** テンプレ一覧グリッド */
function TemplateGrid({
  templates,
  isLoading,
  onPick,
  onDelete,
  disabled,
  visualIdentity,
}: {
  templates: LandingSectionTemplateRecord[];
  isLoading: boolean;
  onPick: (tpl: LandingSectionTemplateRecord) => void;
  onDelete: (tpl: LandingSectionTemplateRecord) => void;
  disabled: boolean;
  visualIdentity: VisualIdentity | undefined;
}) {
  if (isLoading) {
    return (
      <div className="py-10 flex items-center justify-center text-[11px] text-[var(--text-muted)]">
        <Loader2 className="size-4 animate-spin mr-2" />
        読み込み中...
      </div>
    );
  }
  if (templates.length === 0) {
    return (
      <div className="py-10 text-center text-[11px] text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md">
        まだテンプレートがありません。
        <div className="mt-1">
          セクションの操作メニュー内の{' '}
          <Bookmark className="size-3 inline" /> アイコンから保存できます。
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
      {templates.map((tpl) => (
        <TemplateCard
          key={tpl.id}
          tpl={tpl}
          onPick={() => onPick(tpl)}
          onDelete={() => onDelete(tpl)}
          disabled={disabled}
          visualIdentity={visualIdentity}
        />
      ))}
    </div>
  );
}

function TemplateCard({
  tpl,
  onPick,
  onDelete,
  disabled,
  visualIdentity,
}: {
  tpl: LandingSectionTemplateRecord;
  onPick: () => void;
  onDelete: () => void;
  disabled: boolean;
  visualIdentity: VisualIdentity | undefined;
}) {
  const Comp = getSectionComponent(tpl.type as SectionType, tpl.variant);
  const sectionLabel = typeLabel(tpl.type as SectionType);
  const variantLbl = variantLabel(tpl.type as SectionType, tpl.variant);

  const identity: VisualIdentity = visualIdentity ?? 'light';
  const identityCss = identityVars(identity, null);

  return (
    <div className="border border-[var(--border)] rounded-md bg-[var(--bg-elevated)] overflow-hidden flex flex-col">
      {/* 縮小プレビュー: カード幅にフィットさせて左右余白を消す */}
      <div
        className="relative bg-[var(--bg-root)] border-b border-[var(--border)] overflow-hidden cursor-pointer"
        style={{ height: 180 }}
        onClick={() => !disabled && onPick()}
      >
        {Comp ? (
          <ScaledPreview referenceWidth={1280}>
            <div
              data-ls-identity={identity}
              style={
                {
                  ...(identityCss as Record<string, string>),
                  width: 1280,
                } as React.CSSProperties
              }
            >
              <Comp content={tpl.content as never} style={tpl.style as never} />
            </div>
          </ScaledPreview>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[var(--text-muted)]">
            プレビュー描画不可
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-black/60 text-white z-10">
          {sectionLabel}
          <span className="opacity-70"> · {variantLbl}</span>
        </div>
      </div>
      <div className="p-2.5 flex-1 flex flex-col gap-1">
        <div className="text-xs font-semibold line-clamp-1">{tpl.name}</div>
        {tpl.description && (
          <div className="text-[10px] text-[var(--text-muted)] line-clamp-2">
            {tpl.description}
          </div>
        )}
        <div className="text-[10px] text-[var(--text-muted)] mt-auto">
          {tpl.createdByName ?? '匿名'}
        </div>
        <div className="flex items-center gap-1 mt-1">
          <Button size="sm" onClick={onPick} disabled={disabled} className="flex-1">
            このテンプレを挿入
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-[var(--text-muted)] hover:text-red-500"
            title="このテンプレを削除"
          >
            <Trash2 className="size-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
